import React, { forwardRef, useContext, useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { Form } from "react-bootstrap";
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Controller, useForm } from "react-hook-form";
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import Context from "../context";
import { createUser, fetchRoles } from "../service";
import { theme } from "../../../theme";
import BtnSpinner from "../../../components/btnSpinner";

// Wrap InputMask with React.forwardRef
const MaskedInput = forwardRef((props, ref) => {
    const { mask, maskChar, onChange, value, ...otherProps } = props;
    return (
        <InputMask
            mask={mask}
            maskChar={maskChar}
            value={value}
            onChange={onChange}
            {...otherProps}
        >
            {(inputProps) => <input {...inputProps} ref={ref} />}
        </InputMask>
    );
});

const CreateForm = () => {
    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState([]);

    const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm();

    const context = useContext(Context);

    useEffect(() => {
        const getRoles = async () => {
            setLoading(true);
            try {
                const response = await fetchRoles();
                setRoles(response);
            } catch (error) {
                console.error('Error fetching roles:', error);
            } finally {
                setLoading(false);
            }
        };

        getRoles();
    }, []);

    return(
        <div className="ps-4 pe-4">
            <Form className="mt-3" onSubmit={handleSubmit((data) => createUser(data, context.dispatch, reset))}>
                <FormControl error={errors.admin_full_name} fullWidth>
                    <strong>Full name</strong>
                    <TextField
                        error={errors.admin_full_name}
                        placeholder="Full name"
                        size="small"
                        type="text"
                        {...register("admin_full_name", { required: true })}
                    />
                    {errors.admin_full_name && <FormHelperText>Full name is required</FormHelperText>}
                </FormControl>

                <FormControl error={errors.admin_contact_number} fullWidth className="mt-3">
                    <strong>Contact number</strong>
                    <Controller
                        name="admin_contact_number"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                placeholder="Phone number"
                                size="small"
                                InputProps={{
                                    inputComponent: MaskedInput,
                                    inputProps: {
                                        mask: "+44 799 999 9999",
                                        maskChar: "",
                                    },
                                }}
                                error={!!errors.admin_contact_number}
                            />
                        )}
                    />
                    {errors.admin_contact_number && <FormHelperText>Contact number is required</FormHelperText>}
                </FormControl>

                <FormControl error={errors.admin_email} fullWidth className="mt-3">
                    <strong>Email</strong>
                    <TextField
                        error={errors.admin_email}
                        placeholder="Email"
                        size="small"
                        type="email"
                        {...register("admin_email", { required: true })}
                    />
                    {errors.admin_email && <FormHelperText>Email is required</FormHelperText>}
                </FormControl>

                <FormControl error={errors.admin_role} fullWidth className="mt-3">
                    <strong>Role</strong>
                    <Select
                        size="small"
                        displayEmpty
                        defaultValue=""
                        {...register("admin_role", { required: true })}
                    >
                        <MenuItem value="" disabled>Select role</MenuItem>
                        {roles.map((role) => (
                            <MenuItem key={role.role_id} value={role.role_id}>
                                {role.role_name}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.admin_role && <FormHelperText>Role is required</FormHelperText>}
                </FormControl>

                <FormControl error={errors.admin_password} fullWidth className="mt-3">
                    <strong>Password</strong>
                    <TextField
                        error={errors.admin_password}
                        placeholder="Password"
                        size="small"
                        type="password"
                        {...register("admin_password", { required: true })}
                    />
                    {errors.admin_password && <FormHelperText>Password is required</FormHelperText>}
                </FormControl>

                <div className="mt-4">
                    <Button 
                        type="submit" 
                        variant="contained" 
                        fullWidth
                        color={theme.action_btn} 
                        disabled={loading || context.state.is_creating_new_user}
                    >
                        {loading || context.state.is_creating_new_user ? (
                            <BtnSpinner/>
                        ): "CREATE NEW USER"}
                    </Button>
                </div> 
            </Form>
        </div>
    )
}

export default CreateForm;